<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Usuarios do painel</h1>
      <button class="btn-primary" @click="openCreate">+ Novo usuario</button>
    </div>

    <p class="page-help">
      Gerencie quem pode acessar o painel. <strong>Administradores</strong> podem cadastrar/editar participantes e ver pagamentos.
      <strong>Voluntarios</strong> so veem a listagem e usam o scanner pra fazer check-in (sem ver valores financeiros).
    </p>

    <SkeletonLoader v-if="loading" variant="table-row" :count="6" />

    <div v-else-if="admins.length === 0" class="card empty">
      <p>Nenhum usuario cadastrado ainda.</p>
    </div>

    <div v-else class="table-card card">
      <table class="table">
        <thead>
          <tr>
            <th>Nome</th>
            <th>Email</th>
            <th>Papel</th>
            <th>Acoes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in admins" :key="u.id">
            <td>{{ u.name }}</td>
            <td class="cell-email">{{ u.email }}</td>
            <td>
              <span class="role-chip" :class="`role-chip-${u.role.toLowerCase()}`">{{ roleLabel(u.role) }}</span>
            </td>
            <td>
              <div class="action-btns">
                <button class="btn-icon-sm" :disabled="!canChangePasswordFor(u)" @click="openPwd(u)" title="Trocar senha">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2s2 .9 2 2s-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1c1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
                </button>
                <button class="btn-icon-sm btn-icon-sm-danger" :disabled="!canRemove(u)" @click="confirmDelete(u)" title="Remover">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12Z"/></svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create -->
    <div class="modal-overlay" v-if="showCreate" @click.self="closeCreate">
      <div class="modal-card">
        <h2 class="modal-title">Novo usuario</h2>
        <form @submit.prevent="handleCreate">
          <div class="form-row">
            <label class="label">Nome</label>
            <input class="input" v-model="form.name" required maxlength="120" />
          </div>
          <div class="form-row">
            <label class="label">Email</label>
            <input class="input" type="email" v-model="form.email" required />
          </div>
          <div class="form-row">
            <label class="label">Senha</label>
            <input class="input" type="password" v-model="form.password" required minlength="6" />
          </div>
          <div class="form-row">
            <label class="label">Papel</label>
            <select class="input" v-model="form.role">
              <option value="CHECKIN">Voluntario (so check-in)</option>
              <option v-if="authStore.isRoot" value="ADMIN">Administrador</option>
            </select>
            <small class="hint" v-if="!authStore.isRoot">Voce so pode criar voluntarios. Para criar admins, peça ao usuario root.</small>
          </div>
          <div v-if="createError" class="error-msg">{{ createError }}</div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeCreate">Cancelar</button>
            <button type="submit" class="btn-primary" :disabled="creating">{{ creating ? 'Salvando...' : 'Criar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Password -->
    <div class="modal-overlay" v-if="pwdTarget" @click.self="pwdTarget = null">
      <div class="modal-card">
        <h2 class="modal-title">Trocar senha</h2>
        <p class="page-help">Usuario: <strong>{{ pwdTarget.name }}</strong></p>
        <form @submit.prevent="handlePwd">
          <div class="form-row">
            <label class="label">Nova senha</label>
            <input class="input" type="password" v-model="newPassword" required minlength="6" />
          </div>
          <div v-if="pwdError" class="error-msg">{{ pwdError }}</div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="pwdTarget = null">Cancelar</button>
            <button type="submit" class="btn-primary" :disabled="changing">{{ changing ? 'Salvando...' : 'Atualizar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <ConfirmDialog
      :visible="deleteTarget !== null"
      :title="`Remover ${deleteTarget?.name || ''}?`"
      message="Esta acao nao pode ser desfeita."
      confirmLabel="Remover"
      @confirm="handleDelete"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script>
import api from '../lib/api'
import { useAuthStore } from '../stores/auth'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import ConfirmDialog from '../components/ui/ConfirmDialog.vue'

const ROLE_LABEL = { ROOT: 'Root', ADMIN: 'Administrador', CHECKIN: 'Voluntario' }

export default {
  name: 'UsuariosPage',
  components: { SkeletonLoader, ConfirmDialog },
  data() {
    return {
      admins: [],
      loading: false,
      showCreate: false,
      creating: false,
      createError: '',
      form: { name: '', email: '', password: '', role: 'CHECKIN' },
      pwdTarget: null,
      newPassword: '',
      changing: false,
      pwdError: '',
      deleteTarget: null
    }
  },
  computed: {
    authStore() { return useAuthStore() }
  },
  mounted() { this.fetch() },
  methods: {
    roleLabel(role) { return ROLE_LABEL[role] || role },
    canRemove(u) {
      if (u.id === this.authStore.admin?.id) return false
      if (u.role === 'ROOT') return false
      if (this.authStore.isRoot) return true
      if (this.authStore.isAdmin) return u.role === 'CHECKIN'
      return false
    },
    canChangePasswordFor(u) {
      if (u.id === this.authStore.admin?.id) return true
      if (this.authStore.isRoot) return true
      if (this.authStore.isAdmin) return u.role === 'CHECKIN'
      return false
    },
    async fetch() {
      this.loading = true
      try {
        const { data } = await api.get('/admins')
        this.admins = data.admins
      } catch {
        this.$toast.open({ message: 'Falha ao carregar usuarios', type: 'error', position: 'top-right' })
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = { name: '', email: '', password: '', role: 'CHECKIN' }
      this.createError = ''
      this.showCreate = true
    },
    closeCreate() { this.showCreate = false },
    async handleCreate() {
      this.creating = true
      this.createError = ''
      try {
        await api.post('/admins', this.form)
        await this.fetch()
        this.closeCreate()
        this.$toast.open({ message: 'Usuario criado', type: 'success', position: 'top-right' })
      } catch (err) {
        this.createError = err.response?.data?.message || 'Erro ao criar'
      } finally {
        this.creating = false
      }
    },
    openPwd(u) {
      this.pwdTarget = u
      this.newPassword = ''
      this.pwdError = ''
    },
    async handlePwd() {
      this.changing = true
      this.pwdError = ''
      try {
        await api.patch(`/admins/${this.pwdTarget.id}/password`, { password: this.newPassword })
        this.pwdTarget = null
        this.$toast.open({ message: 'Senha atualizada', type: 'success', position: 'top-right' })
      } catch (err) {
        this.pwdError = err.response?.data?.message || 'Erro ao atualizar'
      } finally {
        this.changing = false
      }
    },
    confirmDelete(u) { this.deleteTarget = u },
    async handleDelete() {
      const u = this.deleteTarget
      this.deleteTarget = null
      try {
        await api.delete(`/admins/${u.id}`)
        this.admins = this.admins.filter(a => a.id !== u.id)
        this.$toast.open({ message: 'Usuario removido', type: 'info', position: 'top-right' })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro ao remover', type: 'error', position: 'top-right' })
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-base);
}
.page-header .page-title { margin-bottom: 0; }
.page-help {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  margin-bottom: var(--spacing-base);
  line-height: 1.5;
}
.empty {
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--color-text-muted);
}
.table-card { padding: 0; overflow-x: auto; }
.table {
  width: 100%;
  border-collapse: collapse;
}
.table th {
  text-align: left;
  padding: 12px var(--spacing-base);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid var(--color-bg-separator);
}
.table td {
  padding: 14px var(--spacing-base);
  font-size: var(--text-base);
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-bg-separator);
}
.cell-email { color: var(--color-text-muted); font-size: var(--text-sm); }
.role-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
}
.role-chip-root { background: #fde68a; color: #78350f; }
.role-chip-admin { background: rgba(51,102,204,0.12); color: var(--color-accent); }
.role-chip-checkin { background: var(--color-bg-body); color: var(--color-text-secondary); }
.action-btns { display: flex; gap: var(--spacing-sm); }
.btn-icon-sm {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-bg-hover);
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
}
.btn-icon-sm:hover:not(:disabled) { background: var(--color-accent); color: #fff; }
.btn-icon-sm-danger:hover:not(:disabled) { background: var(--color-danger); color: #fff; }
.btn-icon-sm:disabled { opacity: 0.3; cursor: not-allowed; }
.form-row { margin-bottom: var(--spacing-base); }
.hint { display: block; margin-top: var(--spacing-xs); font-size: var(--text-xs); color: var(--color-text-muted); }
</style>
